import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";
import { StorageHelper } from "../utils/storageHelper";

const LOGIN_WITH_KEYCLOAK = gql`
  mutation LoginWithKeycloak($username: String!, $password: String!) {
    loginWithKeycloak(username: $username, password: $password) {
      access_token
      refresh_token
      user {
        user_id
        username
        email
        mobile
        designation
        first_name
        last_name
        reporting_manager_id
        employee_code
        department
        is_active
        created_on
        updated_on
        role_id
      }
    }
  }
`;

const GET_USERS = gql`
  query GetUsers {
    GetUsers {
      user_id
      username
      email
      first_name
      last_name
      is_active
      designation
      role_id
      created_on
      updated_on
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($userId: ID!, $roleId: Int!) {
    UpdateUser(user_id: $userId, role_id: $roleId) {
      user_id
      role_id
    }
  }
`;

const cacheOperations = {
  writeUsers: (users) => {
    if (!users) return;
    client.writeQuery({
      query: GET_USERS,
      data: {
        GetUsers: users,
      },
    });
  },
  readUsers: () => {
    try {
      const { GetUsers } =
        client.readQuery({
          query: GET_USERS,
        }) || {};
      return GetUsers || [];
    } catch {
      return [];
    }
  },
  updateUsersInCache: (updatedUser) => {
    const existingUsers = cacheOperations.readUsers();
    const updatedUsers = existingUsers.map((user) =>
      user.user_id === updatedUser.user_id ? updatedUser : user
    );
    cacheOperations.writeUsers(updatedUsers);
  },
};

export const useUserApi = () => {
  const [loginMutation, { loading, error, data }] =
    useMutation(LOGIN_WITH_KEYCLOAK);

  const [updateUserMutation] = useMutation(UPDATE_USER);

  const loginWithKeycloak = async (username, password) => {
    try {
      const { data } = await loginMutation({
        variables: { username, password },
      });
      if (!data?.loginWithKeycloak) {
        throw new Error("Login failed");
      }

      const { access_token, refresh_token, user } = data.loginWithKeycloak;

      // Store tokens and user info
      StorageHelper.set("token", access_token);
      StorageHelper.set("refreshToken", refresh_token);
      StorageHelper.set("userInfo", user);

      // Dispatch storage event to notify other components
      window.dispatchEvent(new Event("storage"));

      return {
        success: true,
        data: {
          access_token,
          refresh_token,
          user,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred during login",
      };
    }
  };

  const getUsers = async () => {
    try {
      const { data } = await client.query({
        query: GET_USERS,
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetUsers };
    } catch (error) {
      console.error("Error fetching users:", error);
      return { success: false, error: error.message };
    }
  };

  const updateUserRole = async ({ user_id, role_id }) => {
    try {
      const { data } = await updateUserMutation({
        variables: { userId: String(user_id), roleId: Number(role_id) },
      });

      const updated = data?.UpdateUser;
      if (!updated)
        return { success: false, error: "Failed to update user role" };

      // Keep list in sync without forcing a refetch (optional)
      cacheOperations.updateUsersInCache(updated);

      return { success: true, data: updated };
    } catch (err) {
      return {
        success: false,
        error: err.message || "Failed to update user role",
      };
    }
  };

  return {
    loginWithKeycloak,
    loading,
    error,
    data,
    getUsers,
    updateUserRole,
  };
};
